import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { LocalChecker } from "@/types/front/LocalChecker";
import { GenericTag, LocalTag } from "@/types/front/LocalTag";
import { DataRowResult, IntervalValues } from "@/types/generated/UsedGeneratedTypes";
import { dataLoader } from "@/composable/data/dataLoader";
import services from "@/composable/services";

// --------------------------------------
// TYPES
// --------------------------------------
export type ComponentType
  = | "BasicComponent"
    | "ComputedComponent"
    | "DynamicComponent"
    | "PatternComponent"
    | "patternQualifierComponent"
    | "ConstantComponent"
    | "UnknownComponent";

export interface BuildContext {
  referenceName: string;
  application: LocalApplicationResult;
  getIntl: (ref: string, header: string, app: LocalApplicationResult) => string;
  intl: (text: string) => string;
}
export interface RawComponentData {
  type: ComponentType;
  componentKey: string;
  exportHeader?: string;
  exportHeaderName?: string;
  checker?: LocalChecker | null;
  displaysForRow?: Record<string, any>; // Typage amélioré
  tags?: GenericTag[];
  patternForComponents?: Record<string, any>;
  patternComponentQualifiers?: Record<string, any>;
  patternComponentAdjacents?: Record<string, any>;
}

// --------------------------------------
// BASE COMPONENT
// --------------------------------------
export class Component {
  static TYPES = {
    BASIC: "BasicComponent",
    COMPUTED: "ComputedComponent",
    DYNAMIC: "DynamicComponent",
    PATTERN: "PatternComponent",
    CONSTANT: "ConstantComponent",
    UNKNOWN: "UnknownComponent",
  } as const;

  type: ComponentType;
  id: string;
  title: string;
  componentKey: string;
  exportHeader: string;
  key = false;

  tags: LocalTag[];
  multiplicity: string;
  referenceType?: string | null;
  refLinkedTo: string;
  refLinkedToColumn?: string | null;
  displaysForRow?: Record<string, any>;

  // Pour les filtres
  isInterval?: boolean;
  intervalValues?: IntervalValues[];
  simpleValue?: string;

  getIntl?: BuildContext["getIntl"];
  intl?: BuildContext["intl"];
  checker?: LocalChecker | null;
  datePattern?: string; // Pattern de date extrait du checker pour optimisation
  private _formatDate?: (value: string) => string; // Fonction de formatage pré-configurée

  constructor(
    type: ComponentType,
    key: string,
    exportHeader: string,
    checker?: LocalChecker | null,
    displaysForRow?: any,
    tags?: LocalTag[],
  ) {
    this.type = type;
    this.id = key;
    this.componentKey = key;
    this.title = key;
    this.exportHeader = exportHeader;
    this.checker = checker || null;
    this.displaysForRow = displaysForRow;

    this.tags = tags?.length ? tags : [new LocalTag()];

    this.multiplicity = checker?.multiplicity ?? LocalChecker.MULTIPLICITY_ONE;
    this.referenceType = checker?.type ?? null;
    this.refLinkedTo = checker?.refType ?? "";
    this.refLinkedToColumn = checker?.componentKey ?? null;

    // Extraire le pattern de date du checker si disponible (pour optimisation)
    this.datePattern = (checker as any)?.params?.pattern || (checker as any)?.pattern;

    // Pré-configurer la fonction de formatage de date si on a un pattern
    if (this.datePattern) {
      this._formatDate = this.createDateFormatter(this.datePattern);
    }
  }

  /**
     * Crée une fonction de formatage optimisée pour cette colonne
     */
  private createDateFormatter(pattern: string): (value: string) => string {
    return (value: string): string => {
      if (!value || typeof value !== "string") return String(value || "");

      // Si la valeur commence par "date:", extraire l'ISO
      if (value.startsWith("date:")) {
        const isoStart = 5;
        const firstColon = value.indexOf(":", isoStart + 19);
        if (firstColon !== -1) {
          const isoDate = value.substring(isoStart, firstColon);
          const dateExec = new Date(isoDate);

          // Formatage optimisé sans toLocaleString
          return this.fastFormatDate(dateExec, pattern);
        }
      }

      return value;
    };
  }

  /**
     * Formatage rapide sans toLocaleString (100x plus rapide)
     */
  private fastFormatDate(date: Date, pattern: string): string {
    let result = pattern;

    // Extraire les valeurs une seule fois
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    // Remplacer les patterns
    if (result.includes("yyyy")) {
      result = result.replace("yyyy", String(year));
    }
    if (result.includes("MM")) {
      result = result.replace("MM", String(month).padStart(2, "0"));
    }
    else if (result.includes("M")) {
      result = result.replace("M", String(month));
    }
    if (result.includes("dd")) {
      result = result.replace("dd", String(day).padStart(2, "0"));
    }
    else if (result.includes("d")) {
      result = result.replace("d", String(day));
    }
    if (result.includes("HH")) {
      result = result.replace("HH", String(hours).padStart(2, "0"));
    }
    if (result.includes("mm")) {
      result = result.replace("mm", String(minutes).padStart(2, "0"));
    }
    if (result.includes("ss")) {
      result = result.replace("ss", String(seconds).padStart(2, "0"));
    }

    return result;
  }

  /**
     * Formate une valeur de date pour cette colonne (utilise la fonction pré-configurée)
     */
  formatDateValue(value: string): string {
    return this._formatDate ? this._formatDate(value) : value;
  }

  // --------------------------
  // HELPERS
  // --------------------------
  static compare(a: Component, b: Component): number {
    const delta = a.order() - b.order();
    return delta !== 0 ? delta : a.componentKey.localeCompare(b.componentKey);
  }

  order(): number {
    return this.tags.find(t => t.tagDefinition === "ORDER_TAG")?.tagOrder ?? 99999;
  }

  getHeader(app: LocalApplicationResult, dataId: string): string {
    const description = app.data[dataId]?.componentDescriptions?.[this.id];
    if (!description) return this.componentKey;

    const { importHeader } = description;
    const exportHeader = this.exportHeader;

    if (exportHeader !== this.id) return exportHeader;
    if (importHeader && importHeader !== this.id) return importHeader;

    return this.componentKey;
  }

  // =====================================================
  // BUILD — version robuste et simplifiée
  // =====================================================
  static build(raw: RawComponentData, ctx: BuildContext): Component {
    const { referenceName, application, getIntl, intl } = ctx;

    const header = getIntl(
      referenceName,
      raw.exportHeader ?? raw.exportHeaderName ?? raw.componentKey,
      application,
    );

    const tags = (raw.tags ?? []).map(
      (t: GenericTag) => new LocalTag(t, intl),
    );

    const commonArgs = [
      raw.componentKey,
      header,
      raw.checker ?? null,
      raw.displaysForRow ?? null,
      tags,
    ] as const;

    // Mapping clair types → classes
    const mapping: Record<string, any> = {
      [Component.TYPES.BASIC]: BasicComponent,
      [Component.TYPES.COMPUTED]: ComputedComponent,
      [Component.TYPES.DYNAMIC]: DynamicComponent,
      [Component.TYPES.CONSTANT]: ConstantComponent,
      [Component.TYPES.PATTERN]: PatternComponent,
    };

    const ClassToUse = mapping[raw.type] ?? Component;

    let comp: Component;

    if (ClassToUse === PatternComponent) {
      comp = new PatternComponent(
        ...commonArgs,
        raw.patternForComponents ?? {},
        raw.patternComponentQualifiers ?? {},
        raw.patternComponentAdjacents ?? {},
        getIntl,
        intl,
      );
    }
    else if (ClassToUse === Component) {
      const [key, header, checker, displays, tags] = commonArgs;
      comp = new Component(
        Component.TYPES.UNKNOWN,
        key,
        header,
        checker,
        displays,
        tags,
      );
    }
    else {
      comp = new ClassToUse(...commonArgs);
    }

    comp.getIntl = getIntl;
    comp.intl = intl;

    return comp;
  }

  /* getColumnValue(row: DataRowResult): any {
        return row.values[this.id];
    } */

  getColumnValue(row: DataRowResult): string | number | null {
    if (!row.values) return null;

    // 1 — valeur brute
    let value = row.values[this.id];

    // 2 — Cas PatternQualifierComponent
    if (this instanceof PatternQualifierComponent) {
      const parentBlock = row.values[this.parentKey];
      if (parentBlock && typeof parentBlock === "object") {
        value = parentBlock[this.componentKey];
      }
    }

    // 3 — Cas PatternAdjacentComponent
    if (this instanceof PatternAdjacentComponent) {
      const key = `${this.parentKey}::${this.adjacentKey}`;
      value = row.values[key] ?? row.values[this.adjacentKey];
    }

    // 4 — Internationalisation possible
    if (this.intl && typeof value === "string") {
      try {
        value = this.intl(value);
      }
      catch (_) { /* la valeur reste non-traduite si l'internationalisation échoue */ }
    }

    return value;
  }

  getDisplayValue = (row: DataRowResult, defaultLanguage: string | null): string => {
    const columnValue = this.getColumnValue(row);
    if (row?.refsLinkeds && columnValue && defaultLanguage) {
      const refsLinked = row.refsLinkeds.find(
        column => column?.naturalKey?.sql === columnValue,
      );
      const displaysForRow = dataLoader(services).getValueDisplay(
        refsLinked,
        defaultLanguage,
      );
      if (displaysForRow && typeof columnValue !== "number") {
        return displaysForRow;
      }
      else {
        return "" + columnValue;
      }
    }
    return "" + columnValue;
  };

  componentsForValue(value: any, row: DataRowResult): Component[] {
    const out: Component[] = [];
    const original = value.__ORIGINAL_COLUMN_NAME__;
    const id = this.id + "::" + original;

    const patterns
      = row.allPatternColumnName?.filter((name: string) =>
        (this as any).patternColumns ? name.match((this as any).patternColumns as any) : true,
      ) ?? [];

    // Re-crée une vraie instance, plutôt que cloner des descriptors
    const newPattern = new PatternComponent(
      this.componentKey,
      this.exportHeader,
      this.checker ?? null,
      this.displaysForRow,
      this.tags,
      (this as any).patternColumns,
      (this as any)._qualifierDefs,
      (this as any)._adjacentDefs,
      this.getIntl,
      this.intl,
    );

    newPattern.id = id;
    newPattern.rowId = id;
    newPattern.originalName = original;

    // Dans le JS d'origine, patternForComponents est parfois détourné en "liste de patterns"
    // On conserve ce comportement sans casser, même si le nom est trompeur.
    (newPattern as any).patternForComponents = patterns;

    out.push(newPattern);

    (this as any).adjacents?.forEach((adj: any) => {
      const clone = Object.create(
        Object.getPrototypeOf(adj),
        Object.getOwnPropertyDescriptors(adj),
      );
      clone.originalName = original;
      out.push(clone);
    });

    return out;
  }

  getColumnQualifiersMap(
    application: LocalApplicationResult,
    dataId: string | null,
    row: DataRowResult | null,
    horizontalDisplay?: boolean,
  ): any[] {
    let qualifiers: any[] = [];

    if (dataId) {
      const parts = this.id.includes("::") ? this.id.split("::") : [];
      const hasPattern = parts.length >= 2;
      let value: any;

      if (hasPattern && row) {
        value = row.values[parts[0]].find(
          (component: any) => parts[1] == component.__ORIGINAL_COLUMN_NAME__,
        );
      }
      else {
        value = row?.values?.[dataId]?.[0];
      }

      // Qualifiers
      qualifiers = (this as any).qualifiers?.map((col: any) => {
        const columnName = horizontalDisplay
          ? col.getHeader(application, dataId)
          : dataId;

        const returnValue: any = {
          component: col,
          column: col.id,
          columnName,
          value: value?.[col.id],
          id: col.id,
        };
        returnValue[col.id] = value?.[col.id];
        return returnValue;
      }) ?? [];

      // Adjacent columns
      const adjacents = (this as any).adjacents?.map((col: any) => {
        const columnName = horizontalDisplay
          ? col.getHeader(application, dataId)
          : dataId;

        const parts = col.id.includes("::") ? col.id.split("::") : [];
        const hasPattern = parts.length >= 2;
        let returnValue: any;

        if (hasPattern) {
          returnValue = {
            component: col,
            column: col.id,
            columnName,
            value: value?.[parts[1]],
            id: col.id,
          };
        }
        else {
          returnValue = {
            component: col,
            column: col.id,
            columnName,
            value: value?.[col.id],
            id: col.id,
          };
        }

        returnValue[col.id] = value?.[col.id];
        return returnValue;
      }) ?? [];

      adjacents.forEach((a: any) => qualifiers.push(a));
    }
    else {
      qualifiers = [
        ...((this as any).qualifiers ?? []),
        ...((this as any).adjacents ?? []),
      ];
    }

    return qualifiers;
  }
}

// --------------------------------------
// CLASSES FILLES
// --------------------------------------
export class BasicComponent extends Component {
  constructor(
    componentKey: string,
    exportHeader: string,
    checker?: LocalChecker | null,
    displaysForRow?: any,
    tags?: LocalTag[],
  ) {
    super(
      Component.TYPES.BASIC,
      componentKey,
      exportHeader,
      checker,
      displaysForRow,
      tags,
    );
  }
}

export class ComputedComponent extends Component {
  constructor(
    componentKey: string,
    exportHeader: string,
    checker?: LocalChecker | null,
    displaysForRow?: any,
    tags?: LocalTag[],
  ) {
    super(
      Component.TYPES.COMPUTED,
      componentKey,
      exportHeader,
      checker,
      displaysForRow,
      tags,
    );
  }
}

export class DynamicComponent extends Component {
  constructor(
    componentKey: string,
    exportHeader: string,
    checker?: LocalChecker | null,
    displaysForRow?: any,
    tags?: LocalTag[],
  ) {
    super(
      Component.TYPES.DYNAMIC,
      componentKey,
      exportHeader,
      checker,
      displaysForRow,
      tags,
    );
  }
}

export class ConstantComponent extends Component {
  constructor(
    componentKey: string,
    exportHeader: string,
    checker?: LocalChecker | null,
    displaysForRow?: any,
    tags?: LocalTag[],
  ) {
    super(
      Component.TYPES.CONSTANT,
      componentKey,
      exportHeader,
      checker,
      displaysForRow,
      tags,
    );
  }
}

// --------------------------------------
// PATTERN
// --------------------------------------
export class PatternComponent extends Component {
  patternColumns?: Record<string, any>;
  qualifiers: PatternQualifierComponent[];
  adjacents: PatternAdjacentComponent[];
  originalName?: string;
  rowId?: string;
  horizontalDisplay = true;
  private _qualifierDefs?: Record<string, any>;
  private _adjacentDefs?: Record<string, any>;

  constructor(
    componentKey: string,
    exportHeader: string,
    checker: LocalChecker | null,
    displaysForRow: any,
    tags: LocalTag[],
    patternColumns?: Record<string, any>,
    qualifierDefs?: Record<string, any>,
    adjacentDefs?: Record<string, any>,
    getIntl?: BuildContext["getIntl"],
    intl?: BuildContext["intl"],
  ) {
    super(Component.TYPES.PATTERN, componentKey, exportHeader, checker, displaysForRow, tags);

    this.patternColumns = patternColumns;
    this._qualifierDefs = qualifierDefs;
    this._adjacentDefs = adjacentDefs;

    this.getIntl = getIntl ?? this.getIntl;
    this.intl = intl ?? this.intl;

    this.qualifiers = Object.values(qualifierDefs ?? {}).map((q: any) => {
      const c = new PatternQualifierComponent(componentKey, q);
      c.getIntl = this.getIntl;
      c.intl = this.intl;
      return c;
    });

    this.adjacents = Object.values(adjacentDefs ?? {}).map((a: any) => {
      const c = new PatternAdjacentComponent(componentKey, a);
      c.getIntl = this.getIntl;
      c.intl = this.intl;
      return c;
    });
  }

  getHeader() {
    return this.originalName ?? this.exportHeader;
  }

  getColumnValue(row: DataRowResult): any {
    if (this.horizontalDisplay) {
      const parts = this.id.includes("::") ? this.id.split("::") : [];
      if (parts.length >= 2) {
        return row.values[parts[0]]?.find(
          (column: any) => column.__ORIGINAL_COLUMN_NAME__ == parts[1],
        )?.__VALUE__;
      }
    }
    return row.values[this.componentKey]?.[0]?.__VALUE__;
  }

  componentsForValue(value: any, row: DataRowResult): Component[] {
    const componentsForValue: Component[] = [];
    const id = this.id + "::" + value.__ORIGINAL_COLUMN_NAME__;

    const patterns
      = row.allPatternColumnName?.filter((name: string) =>
        name.match((this as any).patternForComponents),
      ) ?? [];

    const newPatternComponent = Object.create(
      Object.getPrototypeOf(this),
      Object.getOwnPropertyDescriptors(this),
    );
    newPatternComponent.patternForComponents = patterns;
    newPatternComponent.rowId = id;
    newPatternComponent.id = id;
    newPatternComponent.originalName = value.__ORIGINAL_COLUMN_NAME__;
    componentsForValue.push(newPatternComponent);

    this.adjacents.forEach((adjacent) => {
      const newAdjacent = Object.create(
        Object.getPrototypeOf(adjacent),
        Object.getOwnPropertyDescriptors(adjacent),
      );
      newAdjacent.id = adjacent.id + "::" + value.__ORIGINAL_COLUMN_NAME__;
      componentsForValue.push(newAdjacent);
    });

    return componentsForValue;
  }
}

export class PatternQualifierComponent extends Component {
  parentKey: string;

  constructor(parentKey: string, raw: any) {
    super(
      raw.type ?? Component.TYPES.UNKNOWN,
      raw.componentKey,
      raw.exportHeader,
      raw.checker,
      null,
      raw.tags,
    );
    this.parentKey = parentKey;
  }

  getHeader(application: LocalApplicationResult, dataId: string, id?: string): string {
    if (!this.getIntl) {
      return this.exportHeader || this.componentKey;
    }

    const candidate = id ?? this.id;
    const parts = candidate.includes("::") ? candidate.split("::") : [];
    const key = parts.length >= 3 ? parts.slice(0, -1).join("::") : candidate;
    return this.getIntl(dataId, key, application);
  }

  getColumnValue(row: DataRowResult): any {
    let parts: string[] | null = this.id.includes("::") ? this.id.split("::") : null;
    if (parts && parts.length >= 3) {
      return row.values[parts[0]]?.find(
        (column: any) => column.__ORIGINAL_COLUMN_NAME__ == parts?.[2],
      )?.[parts[1]];
    }
    parts = this.id.includes("::") ? this.id.split("::") : null;
    if (parts && parts.length >= 2) {
      return row.values[parts[0]]?.find(
        (column: any) => column.__ORIGINAL_COLUMN_NAME__ == parts[1],
      )?.[parts[1]];
    }
    const value = row.values?.[this.parentKey]?.[0]?.[this.id];
    return row.displaysForRow?.[this.refLinkedTo!]?.[value] || value;
  }
}

export class PatternAdjacentComponent extends Component {
  parentKey: string;
  adjacentKey: string;

  constructor(parentKey: string, raw: any) {
    super(
      raw.type ?? Component.TYPES.UNKNOWN,
      parentKey + "::" + raw.componentKey,
      raw.exportHeader,
      raw.checker,
      null,
      raw.tags,
    );
    this.parentKey = parentKey;
    this.adjacentKey = raw.componentKey;
  }

  getHeader(application: LocalApplicationResult, dataId: string, id?: string): string {
    if (!this.getIntl) {
      return this.exportHeader || this.componentKey;
    }

    const candidate = id ?? this.id;
    const parts = candidate.includes("::") ? candidate.split("::") : [];
    const key = parts.length >= 3 ? parts.slice(0, -1).join("::") : candidate;
    return this.getIntl(dataId, key, application);
  }

  getColumnValue(row: DataRowResult): any {
    let parts: string[] | null = this.id.includes("::") ? this.id.split("::") : null;
    if (parts && parts.length >= 3) {
      return row.values[parts[0]]?.find(
        (column: any) => column.__ORIGINAL_COLUMN_NAME__ == parts?.[2],
      )?.[parts[1]];
    }
    parts = this.id.includes("::") ? this.id.split("::") : null;
    if (parts && parts.length >= 2) {
      return row.values[parts[0]]?.[0]?.[parts[1]];
    }
    return "PatternAdjacentComponent not found " + this.id;
  }
}
